import * as React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ArrowRight from "@mui/icons-material/ArrowRight";

const NestedMenuItem = React.forwardRef((props, ref) => {
  const {
    parentMenuOpen,
    label,
    rightIcon = <ArrowRight style={{ fontSize: 16 }} />,
    keepOpen,
    children,
    customTheme,
    className,
    tabIndex: tabIndexProp,
    ContainerProps: ContainerPropsProp = {},
    rightAnchored,
    isMobile = false,
    ...MenuItemProps
  } = props;

  const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

  const menuItemRef = React.useRef(null);
  React.useImperativeHandle(ref, () => menuItemRef.current);

  const containerRef = React.useRef(null);
  React.useImperativeHandle(containerRefProp, () => containerRef.current);

  const menuContainerRef = React.useRef(null);
  const closeTimerRef = React.useRef(null);

  const [isSubMenuOpen, setIsSubMenuOpen] = React.useState(false);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = (delayMs = 200) => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsSubMenuOpen(false);
    }, delayMs);
  };

  const handleMouseEnter = (event) => {
    if (!isMobile) {
      clearCloseTimer();
      setIsSubMenuOpen(true);
    }

    if (ContainerProps?.onMouseEnter) {
      ContainerProps.onMouseEnter(event);
    }
  };

  const handleMouseLeave = (event) => {
    if (!isMobile) {
      scheduleClose(220);
    }

    if (ContainerProps?.onMouseLeave) {
      ContainerProps.onMouseLeave(event);
    }
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Clear any pending close timer
    clearCloseTimer();
    
    // Toggle submenu state
    setIsSubMenuOpen(prev => !prev);
  };

  const isSubmenuFocused = () => {
    const active = containerRef.current?.ownerDocument?.activeElement;

    for (const child of menuContainerRef.current?.children ?? []) {
      if (child === active) {
        return true;
      }
    }
    return false;
  };

  const handleFocus = (event) => {
    if (event.target === containerRef.current) {
      setIsSubMenuOpen(true);
    }

    if (ContainerProps?.onFocus) {
      ContainerProps.onFocus(event);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      return;
    }

    if (isSubmenuFocused()) {
      event.stopPropagation();
    }

    const active = containerRef.current?.ownerDocument?.activeElement;

    if (event.key === "ArrowLeft" && isSubmenuFocused()) {
      containerRef.current?.focus();
    }

    if (
      event.key === "ArrowRight" &&
      event.target === containerRef.current &&
      event.target === active
    ) {
      const firstChild = menuContainerRef.current?.children[0];
      firstChild?.focus();
    }
  };

  // Global click handler to close submenu when clicking outside
  React.useEffect(() => {
    const handleGlobalClick = (event) => {
      if (isSubMenuOpen && containerRef.current && !containerRef.current.contains(event.target)) {
        // Check if click is within any other menu container
        const menuContainers = document.querySelectorAll('[data-menu-container]');
        let isWithinOtherMenu = false;
        for (const container of menuContainers) {
          if (container !== menuContainerRef.current && container.contains(event.target)) {
            isWithinOtherMenu = true;
            break;
          }
        }
        
        if (!isWithinOtherMenu) {
          setIsSubMenuOpen(false);
        }
      }
    };

    if (isSubMenuOpen) {
      document.addEventListener('mousedown', handleGlobalClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, [isSubMenuOpen]);

  React.useEffect(() => () => clearCloseTimer(), []);

  const open = isSubMenuOpen && parentMenuOpen;

  // Close submenu when parent menu closes
  React.useEffect(() => {
    if (!parentMenuOpen) {
      setIsSubMenuOpen(false);
    }
  }, [parentMenuOpen]);


  let tabIndex;
  if (!props.disabled) {
    tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
  }

  // Listen for close events from parent menu
  React.useEffect(() => {
    const handleCloseSubmenu = (event) => {
      // Only close if the event is not from this element itself
      if (event.target !== containerRef.current) {
        setIsSubMenuOpen(false);
      }
    };

    const currentContainer = containerRef.current;
    if (currentContainer) {
      currentContainer.addEventListener('closeSubmenu', handleCloseSubmenu);
    }

    return () => {
      if (currentContainer) {
        currentContainer.removeEventListener('closeSubmenu', handleCloseSubmenu);
      }
    };
  }, []);

  return (
    <div
      {...ContainerProps}
      ref={containerRef}
      data-nested-menu-item
      onFocus={handleFocus}
      tabIndex={tabIndex}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
    >
      <MenuItem
        {...MenuItemProps}
        data-open={!!open || undefined}
        className={className}
        ref={menuItemRef}
        keepOpen={keepOpen}
        onClick={handleClick}
      >
        {label}
        <div style={{ flexGrow: 1 }} />
        {rightIcon}
      </MenuItem>
      <Menu
        PaperProps={{ 
          sx: { 
            zIndex: 10001,
            pointerEvents: 'auto'
          } 
        }}
        keepMounted
        anchorEl={menuItemRef.current}
        anchorOrigin={{
          vertical: "top",
          horizontal: rightAnchored ? "left" : "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: rightAnchored ? "right" : "left"
        }}
        open={!!open}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        onClose={() => setIsSubMenuOpen(false)}
        sx={{ 
          zIndex: 10001,
          pointerEvents: 'none', // Make the backdrop non-blocking
          '& .MuiPaper-root': {
            pointerEvents: 'auto' // But keep the menu itself clickable
          },
          ...customTheme 
        }}
        onMouseEnter={() => clearCloseTimer()}
        onMouseLeave={() => scheduleClose(180)}
      >
        <div ref={menuContainerRef} data-menu-container>
          {children}
        </div>
      </Menu>
    </div>
  );
});

export default NestedMenuItem;
